<template>
  <div class="portfolio-container">
    <NativeScrollableContent
      :id="scrollId"
      class="col-12 pa-0"
      @onScrollY="updateScrollValue"
      height="calc(100vh - 48px)"
      max-height="calc(100vh - 48px)"
    >
      <div class="d-flex justify-center">
        <div class="portfolio-content">
          <StickySearch
            v-if="enableSearch && !isPortfolioEmpty"
            :hide="hideSearch"
            placeholding
            :placeholder="$t('globals.search')"
            v-model="search"
            :scroll-offset="scrollOffset"
          />
          <v-row no-gutters>
            <DocumentCardIterator
              pagination
              group-id="personal"
              v-if="isPersonalVisible"
              :items="sortAnalyses(filterAnalyses(portfolioAnalyses))"
            >
              <template slot="header">
                <h3 class="pa-3">
                  {{ $t('views.portfolio.personal.title') }} ({{ analysesNumber(filterAnalyses(portfolioAnalyses)) }})
                </h3>
              </template>
              <template v-slot:item="{ item }">
                <VerticalCard
                  :key="item.id"
                  :allow-adding-to-reflection="true"
                  :document-data="item"
                />
              </template>
            </DocumentCardIterator>
          </v-row>
          <v-row v-for="{analysesData, group} in groupsWithAnalyses" no-gutters :key="group.id">
            <span class="title-anchor" :id="`scroll-${group.id}`" />
            <DocumentCardIterator
              pagination
              :group-id="group.id"
              :items="sortAnalyses(filterAnalyses(analysesData))"
            >
              <template slot="header">
                <h3 class="pa-3">
                  {{ group.name }} ({{ analysesNumber(filterAnalyses(analysesData)) }})
                </h3>
              </template>
              <template v-slot:item="{ item }">
                <VerticalCard
                  :key="item.id"
                  :allow-adding-to-reflection="true"
                  :document-data="item"
                />
              </template>
            </DocumentCardIterator>
          </v-row>
          <PortfolioEmpty v-if="isPortfolioEmpty" />
        </div>
      </div>
    </NativeScrollableContent>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import DocumentCardIterator from '@/components/cards/DocumentCardIterator';
import PortfolioEmpty from '@/components/pages/Portfolio/PortfolioEmpty';
import { types } from '@/store/rootTypes';
import { mapGroupPortfolioAnalyses } from '@/tools/portfolioUtils';
import HashPositionMixin from '@/mixins/HashPositionMixin';
import VerticalCard from '@/components/cards/documentCards/VerticalCard';
import StickySearch from '@/components/search/StickySearch';
import NativeScrollableContent from '@/shared/components/scrollbar/NativeScrollableContent';
import FocusedElementWatcher from '@/shared/components/scrollbar/FocusedElementWatcher';

export default {
  name: 'Portfolio',
  components: {
    DocumentCardIterator,
    PortfolioEmpty,
    VerticalCard,
    StickySearch,
    NativeScrollableContent,
  },
  mixins: [HashPositionMixin],
  props: {
    enableSearch: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      search: '',
      scrollId: 'portfolio-scroll',
      lastScrollOffset: 0,
      hideSearch: false,
    };
  },
  computed: {
    ...mapGetters({
      groupsPortfolioAnalyses: types.portfolio.getters.groupsPortfolioAnalyses,
      portfolioAnalyses: types.portfolio.getters.portfolioAnalyses,
    }),
    isGroupsVisible() { return this.groupsWithAnalyses && this.groupsWithAnalyses.length > 0; },
    isPersonalVisible() { return this.portfolioAnalyses && this.portfolioAnalyses.length > 0; },
    isPortfolioEmpty() {
      return !this.isGroupsVisible && !this.isPersonalVisible;
    },
    groupsWithAnalyses() {
      return mapGroupPortfolioAnalyses(this.groupsPortfolioAnalyses)
        .sort(({ group: groupA }, { group: groupB }) => groupA.name.localeCompare(groupB.name));
    },
  },
  methods: {
    ...mapActions({
      setAnalysisPagination: types.analyses.actions.setAnalysisPagination,
    }),
    onSetPagination(pagination) {
      this.setAnalysisPagination(pagination);
    },
    sortAnalyses(analyses = []) {
      return [...analyses].sort((a1, a2) => a2.auditInfo.modifiedOn - a1.auditInfo.modifiedOn);
    },
    analysesNumber(analyses = []) {
      return analyses.length;
    },
    filterAnalyses(analyses = []) {
      if (!this.search || this.search === '') return analyses;

      return analyses.filter(analysis => (
        this.isIncludedInSearch(analysis.documentDetails.title)
        || this.isIncludedInSearch(analysis.documentDetails.description)
      ));
    },
    isIncludedInSearch(text) {
      if (!text) return false;
      if (!this.search) return true;

      return text.toLowerCase().includes(this.search.toLowerCase());
    },
    updateScrollValue() {
      const watcher = new FocusedElementWatcher(this.scrollId);
      const scrollOffset = watcher.getScrollOffset();

      this.hideSearch = scrollOffset > this.lastScrollOffset;
      this.lastScrollOffset = scrollOffset;
    },
  },
  watch: {
    groupsWithAnalyses(nextList) {
      if (nextList && nextList.length) {
        this.resetPosition();
      }
    },
  },
};
</script>

<style scoped>
.portfolio-container {
  width: 100%;
}

.portfolio-content {
  max-width: 1864px;
  min-width: 0;
  flex-grow: 1;
}

.title-anchor {
  width: 0;
  height: 0;
  position: relative;
  top: -64px;
}
</style>
